import { useMemo } from 'react';
import { Flex, Text } from '@backstage/ui';
import type { CheckDefinition } from '@internal/backstage-plugin-pulse-common';
import { TrackCheckPicker } from './track-check-picker';
import { FieldError } from './field-error';

export type TrackChecksSectionProps = {
  checks: CheckDefinition[];
  mandatoryCheckIds: string[];
  onMandatoryCheckIdsChange: (checkIds: string[]) => void;
  mandatoryLevelDescription: string;
  onMandatoryLevelDescriptionChange: (description: string) => void;
  recommendedCheckIds: string[];
  onRecommendedCheckIdsChange: (checkIds: string[]) => void;
  recommendedLevelDescription: string;
  onRecommendedLevelDescriptionChange: (description: string) => void;
  onCheckExpanded: (checkId: string) => void;
  expandedCheckId?: string;
  validationAttempt?: number;
  isLoading?: boolean;
  isReadOnly?: boolean;
  mandatoryChecksError?: string;
  hasInvalidMandatoryRows?: boolean;
  hasInvalidRecommendedRows?: boolean;
  duplicateChecksError?: string;
};

/** Mandatory and recommended check pickers for a track. */
export const TrackChecksSection = ({
  checks,
  mandatoryCheckIds,
  onMandatoryCheckIdsChange,
  mandatoryLevelDescription,
  onMandatoryLevelDescriptionChange,
  recommendedCheckIds,
  onRecommendedCheckIdsChange,
  recommendedLevelDescription,
  onRecommendedLevelDescriptionChange,
  onCheckExpanded,
  expandedCheckId,
  validationAttempt,
  isLoading = false,
  isReadOnly = false,
  mandatoryChecksError,
  hasInvalidMandatoryRows = false,
  hasInvalidRecommendedRows = false,
  duplicateChecksError,
}: TrackChecksSectionProps) => {
  // A check belongs to one level only, so each picker hides the other's picks.
  const mandatoryOptions = useMemo(() => {
    const taken = new Set(recommendedCheckIds);
    return checks.filter(check => !taken.has(check.id));
  }, [checks, recommendedCheckIds]);

  const recommendedOptions = useMemo(() => {
    const taken = new Set(mandatoryCheckIds);
    return checks.filter(check => !taken.has(check.id));
  }, [checks, mandatoryCheckIds]);

  return (
    <Flex direction="column" gap="3">
      <Text variant="title-x-small">Choose Checks</Text>
      <Text variant="body-small" color="secondary">
        Add checks to your track by selecting them from the autocomplete. You
        can preview the checks to see their properties.
      </Text>

      <Flex direction="column" gap="1">
        <TrackCheckPicker
          label="Mandatory checks"
          description="Entities must pass every mandatory check to be compliant."
          addButtonLabel="Add mandatory check"
          checks={mandatoryOptions}
          value={mandatoryCheckIds}
          onChange={onMandatoryCheckIdsChange}
          levelDescription={mandatoryLevelDescription}
          onLevelDescriptionChange={onMandatoryLevelDescriptionChange}
          onCheckExpanded={onCheckExpanded}
          expandedCheckId={expandedCheckId}
          isRequired
          validationAttempt={validationAttempt}
          isLoading={isLoading}
          isReadOnly={isReadOnly}
          isInvalid={Boolean(mandatoryChecksError) || hasInvalidMandatoryRows}
        />
        <FieldError message={mandatoryChecksError} />
      </Flex>

      <Flex direction="column" gap="1">
        <TrackCheckPicker
          label="Recommended checks"
          description="Passing the mandatory and recommended checks means the entity is fully compliant. Adding recommended checks is optional."
          addButtonLabel="Add recommended check"
          checks={recommendedOptions}
          value={recommendedCheckIds}
          onChange={onRecommendedCheckIdsChange}
          levelDescription={recommendedLevelDescription}
          onLevelDescriptionChange={onRecommendedLevelDescriptionChange}
          onCheckExpanded={onCheckExpanded}
          expandedCheckId={expandedCheckId}
          isRequired={false}
          validationAttempt={validationAttempt}
          isLoading={isLoading}
          isReadOnly={isReadOnly}
          isInvalid={hasInvalidRecommendedRows}
        />
      </Flex>

      <FieldError message={duplicateChecksError} />
    </Flex>
  );
};
